import { withListOverflow } from "../../v2/components/ListOverflow.jsx";
export const OpenRolesReport = withListOverflow(OpenRolesListOwner);
// src/features/reports/OpenRolesReport.jsx
//
// REPORTS-OPEN-ROLES: Greenhouse-backed open roles report.
// OPEN-ROLES-REPORT-FIX: v2 layout pass + dedup reconciliation.
// OPEN-ROLES-REPORT-MATCH-TEMPLATE: aligned to HeadcountTrendReport shell.
//
// Layout:
//   Shared report header with "‹ Reports" back-nav (Band 1 is the global app header)
//   Body:   5 animated KPI tiles → toolbar (search + view toggle) →
//           freshness bar → Market/Department/Region table
//
// Count: deduplicated by internal_job_id on the server so the KPI
// matches the public Greenhouse careers page.
// Remote split: "remote", "distributed", "anywhere" all land in the
// Remote / Distributed bucket — never inflated to a market row.

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { fmtInt } from "../../utils/format.js";
import { T } from "../../app/tokens.js";
import { S } from "../../app/styles.js";
import { Card } from "../../components/Card.jsx";
import { ErrorBanner } from "../../components/ErrorBanner.jsx";
import { ReportShell } from "./ReportShell.jsx";
import { ReportAccessNotice } from "./ReportShell.jsx";
import { useFeatureFlagContext } from "../../contexts/FeatureFlagContext.jsx";
import { getReportNavEligibility } from "./reportNavEligibility.js";
import { ToolbarViewToggle } from "../../v2/components/ToolbarViewToggle.jsx";
import { useReportViewState } from "../../v2/hooks/useReportViewState.js";
import { REPORT_OPEN_ROLES_KEY } from "../../services/portfolioStatePersistence.js";
import { REPORT_TH_CSS, reportTableStyles as RT, LegendTh, pillIndicator } from "./reportStyles.jsx";
import { ColorLegendPopup } from "../../components/ColorLegendPopup.jsx";
import { KpiTile } from "../../components/KpiTile.jsx";
import { KpiBand } from "../../components/KpiBand.jsx";
import { getOpenRolesMonthlyHistory, getOpenRolesReport } from "../../data/openRolesAdapter.js";
import { OpenRolesMonthlyTrend } from "./OpenRolesMonthlyTrend.jsx";
import { matchesFoldedQuery } from "../../utils/textSearch.js";
import {
  computeSpaceFit,
  computeMarketSpaceFit,
  SPACE_FIT_BG,
  SPACE_FIT_COLOR,
  SPACE_FIT_VERDICT_OVER,
  SPACE_FIT_VERDICT_TIGHT,
  SPACE_FIT_VERDICT_DENSE,
  SPACE_FIT_VERDICT_UNDER,
  SPACE_FIT_VERDICT_RIGHT,
} from "../../utils/spaceFitVerdict.js";
import { computeOpenRolesInsight } from "./insightRules.js";
import { useSpacePolicy } from "../../services/spacePolicyStore.js";
import { HowWeDefineThis } from "../../components/SpacePolicyDefinitions.jsx";
import { CompactInsightStrip } from "./InsightCallout.jsx";
import { getRegionCapacityBuckets, REGION_CAPACITY_BUCKETS } from "./openRolesCapacityBuckets.js";

// ─── Space Fit pill ────────────────────────────────────────────────────────────
// Uses the canonical computeSpaceFit verdict (Right-sized / Dense / Over capacity
// / Under-used) so vocabulary is consistent with the Space Fit report/column.

function SpaceFitPill({ market }) {
  useSpacePolicy();
  const { verdict, dense, partial } = computeMarketSpaceFit(market);
  if (!verdict) return <span style={{ color: T.light }}>—</span>;
  return (
    <span style={{
      display: "inline-block",
      padding: "2px 8px",
      borderRadius: 12,
      fontSize: T.fsXs,
      fontWeight: T.fwSemibold,
      background: SPACE_FIT_BG[verdict]    ?? SPACE_FIT_BG[null],
      color:      SPACE_FIT_COLOR[verdict] ?? SPACE_FIT_COLOR[null],
      whiteSpace: "nowrap",
    }}>
      {verdict}{partial && <small> · Partial</small>}
    </span>
  );
}

// ── Capacity status legend — Space Fit verdicts ────────────────────────────────

const CAPACITY_LEGEND = (
  <ColorLegendPopup rows={[
    { indicator: pillIndicator(SPACE_FIT_BG[SPACE_FIT_VERDICT_OVER],   SPACE_FIT_COLOR[SPACE_FIT_VERDICT_OVER],   SPACE_FIT_VERDICT_OVER),   label: SPACE_FIT_VERDICT_OVER   },
    { indicator: pillIndicator(SPACE_FIT_BG[SPACE_FIT_VERDICT_TIGHT], SPACE_FIT_COLOR[SPACE_FIT_VERDICT_TIGHT], SPACE_FIT_VERDICT_TIGHT), label: SPACE_FIT_VERDICT_TIGHT },
    { indicator: pillIndicator(SPACE_FIT_BG[SPACE_FIT_VERDICT_UNDER],  SPACE_FIT_COLOR[SPACE_FIT_VERDICT_UNDER],  SPACE_FIT_VERDICT_UNDER),  label: SPACE_FIT_VERDICT_UNDER  },
    { indicator: pillIndicator(SPACE_FIT_BG[SPACE_FIT_VERDICT_RIGHT],  SPACE_FIT_COLOR[SPACE_FIT_VERDICT_RIGHT],  SPACE_FIT_VERDICT_RIGHT),  label: SPACE_FIT_VERDICT_RIGHT  },
  ]} footer={<HowWeDefineThis />} />
);

const CAPACITY_LEGEND_TEXT =
  "Space Fit: Over-Cap, Tight, Right-Sized, Under-Used. Dense is a separate density flag.";
const REGION_CAPACITY_LEGEND_TEXT =
  "Counts use each market's Space Fit verdict: Over-Cap = over, Tight = near, Right-Sized = right-sized, Under-Used = room; missing verdicts = no data.";

// ─── Freshness bar ────────────────────────────────────────────────────────────
// Positioned BELOW the search/view-toggle toolbar, ABOVE the table.
// Left: passive relative freshness label. Right: source link.

function FreshnessBar({ freshness }) {
  const { pulledAt, stale, ghUnavailable } = freshness ?? {};

  const ageLabel = useMemo(() => {
    if (!pulledAt) return "never";
    const ms = Date.now() - new Date(pulledAt).getTime();
    const min = Math.floor(ms / 60_000);
    if (min < 1)  return "just now";
    if (min < 60) return `${min}m ago`;
    const hr = Math.floor(min / 60);
    if (hr < 24) return `${hr}h ago`;
    return `${Math.floor(hr / 24)}d ago`;
  }, [pulledAt]);

  return (
    <div
      data-testid="open-roles-freshness-bar"
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 12,
        padding: "9px 14px",
        borderRadius: 10,
        background: stale ? T.utilAmber : T.card,
        border: `1px solid ${T.border ?? T.lightGray}`,
        marginBottom: 14,
        fontSize: T.fsXs,
        color: T.mid,
        flexWrap: "wrap",
      }}
    >
      {/* Left: freshness text */}
      <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
        {/* green dot */}
        <span style={{
          width: 7, height: 7, borderRadius: "50%",
          background: stale ? T.amber : T.utilGreenText ?? T.green,
          display: "inline-block", flexShrink: 0,
        }} />
        <span>
          <span data-testid="open-roles-updated" style={{ fontWeight: T.fwSemibold, color: T.dark }}>
            Updated {ageLabel}
          </span>
          {" · "}source: Greenhouse · refreshes daily
        </span>
        {stale && (
          <span
            data-testid="open-roles-stale-pill"
            style={{
              padding: "1px 8px",
              borderRadius: 10,
              background: T.amber,
              color: T.white,
              fontWeight: T.fwSemibold,
              fontSize: T.fsXxs ?? T.fsXs,
            }}
          >
            {ghUnavailable
              ? `Stale - Greenhouse unavailable · last updated ${ageLabel}`
              : `Stale · last updated ${ageLabel}`}
          </span>
        )}
      </span>

      {/* Right: Greenhouse source link */}
      <span style={{ display: "inline-flex", alignItems: "center", gap: 12 }}>
        <a
          href="https://boards.greenhouse.io/ripple"
          target="_blank"
          rel="noopener noreferrer"
          style={{
            color: T.blue,
            fontWeight: T.fwSemibold,
            fontSize: T.fsXs,
            textDecoration: "none",
            display: "inline-flex",
            alignItems: "center",
            gap: 4,
          }}
        >
          View on Greenhouse ↗
        </a>
      </span>
    </div>
  );
}

// ─── View-by options ──────────────────────────────────────────────────────────

const VIEW_SEGMENTS = [
  { key: "market",     label: "Market",     testid: "open-roles-view-market"     },
  { key: "department", label: "Department", testid: "open-roles-view-department" },
  { key: "region",     label: "Region",     testid: "open-roles-view-region"     },
];

// ─── Shared table sort helper ─────────────────────────────────────────────────

function useSortState(defaultKey, defaultDir = "desc") {
  const [sortKey, setSortKey] = useState(defaultKey);
  const [sortDir, setSortDir] = useState(defaultDir);

  const handleSort = useCallback((key) => {
    setSortKey((prev) => {
      if (prev === key) {
        setSortDir((d) => d === "asc" ? "desc" : "asc");
      } else {
        setSortDir(key === defaultKey ? "desc" : "asc");
      }
      return key;
    });
  }, [defaultKey]);

  return { sortKey, sortDir, handleSort };
}

function applySortDir(arr, key, dir) {
  const copy = [...arr];
  copy.sort((a, b) => {
    const av = a[key] ?? "";
    const bv = b[key] ?? "";
    if (typeof av === "string") {
      return dir === "asc" ? av.localeCompare(bv) : bv.localeCompare(av);
    }
    return dir === "asc" ? av - bv : bv - av;
  });
  return copy;
}

// ─── Open Roles table style constants ─────────────────────────────────────────
// These replicate the exact values from HeadcountTrendReport's TH / TD objects
// so both reports render with the same cell padding, border weight, and font size.

const OR_TABLE = {
  ...RT.table,
  fontSize: T.fsSm5,
};

const OR_TR = {
  borderBottom: "none",
  transition: "background 0.15s",
};

const OR_TD = (align = "left") => ({
  ...RT.td(align),
  padding: "11px 12px",
  borderBottom: `1px solid ${T.bg}`,
  fontSize: T.fsSm5,
  verticalAlign: "middle",
  whiteSpace: "nowrap",
});

// Sticky note: position:"sticky" on <thead> (not individual <th>) works in modern
// browsers when the table is NOT inside an overflowX:auto container. These tables
// are rendered inside <Card> with no overflow:auto ancestor (comment at line ~852
// confirms "no inner scroll container; page scrolls naturally"), so this works.
// If a Card ever gains overflow:auto, this will silently break — switch to the
// two-table split (matching Offices/Projects) at that point.
// TODO: sticky-header slice — consider migrating to individual-<th> sticky or
// the two-table split for full cross-browser safety (sticky on <thead> is not
// in the spec; it works via the "table-group" stickiness extension most browsers
// implement, but it is not guaranteed by the CSS spec).
const OR_THEAD = {
  position: "sticky",
  top: 0,
  zIndex: 2,
  background: "var(--v2-tint)",
  borderBottom: "1px solid var(--v2-line)",
};

// ─── Market table ─────────────────────────────────────────────────────────────

function MarketTable({ rows, remoteCount, unmatchedCount, search, totalReqs }) {
  const { sortKey, sortDir, handleSort } = useSortState("openRoles");

  const filtered = useMemo(() => {
    const q = search.trim();
    if (!q) return rows;
    return rows.filter((r) =>
      matchesFoldedQuery(q, r.marketName, r.region, r.topDepartment)
    );
  }, [rows, search]);

  const sorted = useMemo(() => applySortDir(filtered, sortKey, sortDir), [filtered, sortKey, sortDir]);

  const sortStyle = (key) => ({
    ...RT.th(key === "openRoles" ? "right" : "left"),
    cursor: "pointer",
    userSelect: "none",
    fontSize: T.fsXxs,
    fontWeight: T.fwBold,
    letterSpacing: ".07em",
    textTransform: "uppercase",
    color: sortKey === key ? T.blue : "var(--v2-mid)",
  });

  const totalOffice = totalReqs - remoteCount - unmatchedCount;

  if (sorted.length === 0 && remoteCount === 0 && unmatchedCount === 0) {
    return <div style={S.emptyState}>No markets match your search.</div>;
  }

  const FOOT_TD = {
    padding: "11px 14px",
    fontSize: T.fsSm5,
    fontWeight: T.fwBold,
    color: T.dark,
    borderTop: `2px solid ${T.border}`,
    background: T.card,
  };

  return (
    <>
      <style>{REPORT_TH_CSS}</style>
      <table style={{ ...OR_TABLE, tableLayout: "fixed" }}>
        <colgroup>
          <col style={{ width: "20%" }} />
          <col style={{ width: "10%" }} />
          <col style={{ width: "17%" }} />
          <col style={{ width: "31%" }} />
          <col style={{ width: "22%" }} />
        </colgroup>
        <thead style={OR_THEAD}>
          <tr>
            <th className="report-sort-th" style={sortStyle("marketName")} onClick={() => handleSort("marketName")}>Market</th>
            <th className="report-sort-th" style={sortStyle("region")}     onClick={() => handleSort("region")}>Region</th>
            <th className="report-sort-th" style={{ ...sortStyle("openRoles"), textAlign: "right" }} onClick={() => handleSort("openRoles")}>Open roles</th>
            <th style={{ ...RT.th("left"), fontSize: T.fsXxs, fontWeight: T.fwBold, letterSpacing: ".07em", textTransform: "uppercase", color: "var(--v2-mid)", paddingLeft: 28 }}>Top department</th>
            <LegendTh
              label="Capacity status"
              popup={CAPACITY_LEGEND}
              tooltipId="th-tooltip-capacity-status"
              tooltipText={CAPACITY_LEGEND_TEXT}
              thStyle={{ fontSize: T.fsXxs, fontWeight: T.fwBold, letterSpacing: ".07em", textTransform: "uppercase", color: "var(--v2-mid)" }}
            />
          </tr>
        </thead>
        <tbody>
          {sorted.map((r) => (
            <tr
              key={r.marketId}
              style={OR_TR}
              onMouseEnter={(e) => (e.currentTarget.style.background = T.hoverBg)}
              onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
            >
              <td style={OR_TD()}>
                <span style={{ fontWeight: T.fwSemibold }}>{r.marketName}</span>
              </td>
              <td style={OR_TD()}>{r.region ?? <span style={{ color: T.light }}>—</span>}</td>
              <td style={{ ...OR_TD("right"), fontWeight: T.fwDisplay }}>
                {r.openRoles.toFixed(1)}
              </td>
              <td style={{ ...OR_TD(), paddingLeft: 28 }}>{r.topDepartment ?? <span style={{ color: T.light }}>—</span>}</td>
              <td style={OR_TD()}><SpaceFitPill market={r} /></td>
            </tr>
          ))}

          {/* Remote pinned row */}
          {remoteCount > 0 && (
            <tr
              style={OR_TR}
              onMouseEnter={(e) => (e.currentTarget.style.background = T.hoverBg)}
              onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
            >
              <td style={OR_TD()}>
                <div style={{ fontWeight: T.fwSemibold, fontStyle: "italic", color: T.mid }}>Remote / Distributed</div>
                <div style={{ fontSize: T.fsXs, color: T.light, marginTop: 1 }}>no office - separate bucket</div>
              </td>
              <td style={OR_TD()}><span style={{ color: T.light }}>—</span></td>
              <td style={{ ...OR_TD("right"), fontWeight: T.fwDisplay }}>{remoteCount.toFixed(1)}</td>
              <td style={OR_TD()}><span style={{ color: T.light }}>—</span></td>
              <td style={OR_TD()}><span style={{ color: T.light }}>n/a</span></td>
            </tr>
          )}

          {/* Unmatched row (only when non-zero) */}
          {unmatchedCount > 0 && (
            <tr
              style={OR_TR}
              onMouseEnter={(e) => (e.currentTarget.style.background = T.hoverBg)}
              onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
            >
              <td style={OR_TD()}>
                <div style={{ fontWeight: T.fwSemibold, fontStyle: "italic", color: T.mid }}>Unmatched offices</div>
                <div style={{ fontSize: T.fsXs, color: T.light, marginTop: 1 }}>No matching market found</div>
              </td>
              <td style={OR_TD()}><span style={{ color: T.light }}>—</span></td>
              <td style={{ ...OR_TD("right"), fontWeight: T.fwDisplay }}>{unmatchedCount.toFixed(1)}</td>
              <td style={OR_TD()}><span style={{ color: T.light }}>—</span></td>
              <td style={OR_TD()}><span style={{ color: T.light }}>n/a</span></td>
            </tr>
          )}
        </tbody>
        {/* Total footer row — consistent with Headcount Trend's "= Total company" tfoot pattern */}
        <tfoot>
          <tr>
            <td style={FOOT_TD}>
              <span style={{ color: T.light, marginRight: 6 }}>=</span>
              All markets · total
              <span style={{ fontWeight: T.fwRegular, color: T.mid, marginLeft: 10, fontSize: T.fsXs }}>
                {totalReqs != null ? `${totalReqs} unique reqs · ${typeof totalOffice === "number" ? totalOffice.toFixed(1) : "—"} office-based · ${remoteCount.toFixed(1)} remote · market-allocated (fractionalized)` : ""}
              </span>
            </td>
            <td colSpan={4} style={FOOT_TD} />
          </tr>
        </tfoot>
      </table>
    </>
  );
}

// ─── Department table ─────────────────────────────────────────────────────────

function DepartmentTable({ departments, search, totalReqs, officeBased, remote, marketsEnabled, officesEnabled }) {
  const { sortKey, sortDir, handleSort } = useSortState("total");

  const filtered = useMemo(() => {
    const q = search.trim();
    if (!q) return departments;
    return departments.filter((d) =>
      matchesFoldedQuery(q, d.department, ...(marketsEnabled ? [d.topMarket] : []))
    );
  }, [departments, search, marketsEnabled]);

  const sorted = useMemo(() => applySortDir(filtered, sortKey, sortDir), [filtered, sortKey, sortDir]);

  const numTh = (key) => ({
    ...RT.th("right"),
    cursor: "pointer",
    userSelect: "none",
    fontSize: T.fsXxs,
    fontWeight: T.fwBold,
    letterSpacing: ".07em",
    textTransform: "uppercase",
    color: sortKey === key ? T.blue : "var(--v2-mid)",
  });
  const leftTh = (key) => ({
    ...RT.th("left"),
    cursor: "pointer",
    userSelect: "none",
    fontSize: T.fsXxs,
    fontWeight: T.fwBold,
    letterSpacing: ".07em",
    textTransform: "uppercase",
    color: sortKey === key ? T.blue : "var(--v2-mid)",
  });

  if (sorted.length === 0) {
    return <div style={S.emptyState}>No departments match your search.</div>;
  }

  const FOOT_TD_BASE = { padding: "11px 14px", fontSize: T.fsSm5, fontWeight: T.fwBold, color: T.dark, borderTop: `2px solid ${T.border}`, background: T.card };

  return (
    <>
      <style>{REPORT_TH_CSS}</style>
      <table style={{ ...OR_TABLE, tableLayout: "fixed" }}>
        <colgroup>
          <col style={{ width: marketsEnabled ? "20%" : "34%" }} />
          <col style={{ width: "16%" }} />
          {officesEnabled && <col style={{ width: "16%" }} />}
          <col style={{ width: "16%" }} />
          {marketsEnabled && <col style={{ width: "22%" }} />}
          {marketsEnabled && <col style={{ width: "16%" }} />}
        </colgroup>
        <thead style={OR_THEAD}>
          <tr>
            <th className="report-sort-th" style={leftTh("department")} onClick={() => handleSort("department")}>Department</th>
            <th className="report-sort-th" style={numTh("total")}       onClick={() => handleSort("total")}>Open roles</th>
            {officesEnabled && <th className="report-sort-th" style={numTh("officeBased")} onClick={() => handleSort("officeBased")}>Office-based</th>}
            <th className="report-sort-th" style={numTh("remote")}      onClick={() => handleSort("remote")}>Remote</th>
            {marketsEnabled && <th style={{ ...RT.th("left"), fontSize: T.fsXxs, fontWeight: T.fwBold, letterSpacing: ".07em", textTransform: "uppercase", color: "var(--v2-mid)", paddingLeft: 28 }}>Top market</th>}
            {marketsEnabled && <th style={{ ...RT.th("left"), fontSize: T.fsXxs, fontWeight: T.fwBold, letterSpacing: ".07em", textTransform: "uppercase", color: "var(--v2-mid)" }}>Capacity status</th>}
          </tr>
        </thead>
        <tbody>
          {sorted.map((d) => (
            <tr
              key={d.department}
              style={OR_TR}
              onMouseEnter={(e) => (e.currentTarget.style.background = T.hoverBg)}
              onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
            >
              <td style={{ ...OR_TD(), fontWeight: T.fwSemibold, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{d.department}</td>
              <td style={{ ...OR_TD("right"), fontWeight: T.fwDisplay }}>{d.total.toFixed(1)}</td>
              {officesEnabled && <td style={OR_TD("right")}>{d.officeBased.toFixed(1)}</td>}
              <td style={OR_TD("right")}>{d.remote.toFixed(1)}</td>
              {marketsEnabled && <td style={{ ...OR_TD(), paddingLeft: 28 }}>{d.topMarket ?? <span style={{ color: T.light }}>—</span>}</td>}
              {/* TODO(Corey): decide whether to hide this column, keep "—", or derive from the top market/region */}
              {marketsEnabled && <td style={OR_TD()}><span style={{ color: T.light }}>—</span></td>}
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr>
            <td style={FOOT_TD_BASE}>
              <span style={{ color: T.light, marginRight: 6 }}>=</span>
              All departments · total
            </td>
            <td style={{ ...FOOT_TD_BASE, textAlign: "right" }}>{totalReqs}</td>
            {officesEnabled && <td style={{ ...FOOT_TD_BASE, textAlign: "right" }}>{typeof officeBased === "number" ? officeBased.toFixed(1) : "—"}</td>}
            <td style={{ ...FOOT_TD_BASE, textAlign: "right" }}>{typeof remote === "number" ? remote.toFixed(1) : "—"}</td>
            {marketsEnabled && <td colSpan={2} style={FOOT_TD_BASE} />}
          </tr>
        </tfoot>
      </table>
    </>
  );
}

// ─── Region table ─────────────────────────────────────────────────────────────

const REGION_CAPACITY_STYLES = {
  over: { background: T.ragRedBg, color: T.ragRed },
  near: { background: T.ragAmberBg, color: T.ragAmberText },
  rightSized: { background: T.ragGreenBg, color: T.ragGreenText },
  room: { background: T.blueSoft, color: T.blue },
  noData: { background: T.lightGray, color: T.mid },
};

function RegionCapacityChips({ counts, testId }) {
  return (
    <span
      data-testid={testId}
      aria-label={REGION_CAPACITY_BUCKETS
        .filter(({ key }) => counts[key] > 0)
        .map(({ key, label }) => `${counts[key]} ${label}`)
        .join(", ") || "No markets"}
      style={{ display: "inline-flex", flexWrap: "wrap", alignItems: "center", gap: 5 }}
    >
      {REGION_CAPACITY_BUCKETS.map(({ key, label }) => {
        const count = counts[key];
        if (!count) return null;
        return (
          <span
            key={key}
            data-testid={`${testId}-${key}`}
            style={{
              ...REGION_CAPACITY_STYLES[key],
              display: "inline-flex",
              alignItems: "center",
              gap: 4,
              padding: "3px 7px",
              borderRadius: 10,
              fontSize: T.fsXxs,
              lineHeight: 1.2,
              fontWeight: T.fwSemibold,
              whiteSpace: "nowrap",
            }}
          >
            <span>{count}</span> {label}
          </span>
        );
      })}
    </span>
  );
}

function RegionTable({ regions, search, totalReqs, markets }) {
  const spacePolicyState = useSpacePolicy();
  const { sortKey, sortDir, handleSort } = useSortState("openRoles");

  const filtered = useMemo(() => {
    const q = search.trim();
    if (!q) return regions;
    return regions.filter((r) =>
      matchesFoldedQuery(q, r.region, r.topDept)
    );
  }, [regions, search]);

  const sorted = useMemo(() => applySortDir(filtered, sortKey, sortDir), [filtered, sortKey, sortDir]);

  const totalMarkets = markets.length;
  const { byRegion: capacityByRegion, total: portfolioCapacity } = useMemo(
    () => getRegionCapacityBuckets(markets),
    [markets, spacePolicyState.baseline],
  );

  const numTh = (key) => ({
    ...RT.th("right"),
    cursor: "pointer",
    userSelect: "none",
    fontSize: T.fsXxs,
    fontWeight: T.fwBold,
    letterSpacing: ".07em",
    textTransform: "uppercase",
    color: sortKey === key ? T.blue : "var(--v2-mid)",
  });
  const leftTh = (key) => ({
    ...RT.th("left"),
    cursor: "pointer",
    userSelect: "none",
    fontSize: T.fsXxs,
    fontWeight: T.fwBold,
    letterSpacing: ".07em",
    textTransform: "uppercase",
    color: sortKey === key ? T.blue : "var(--v2-mid)",
  });

  if (sorted.length === 0) {
    return <div style={S.emptyState}>No regions match your search.</div>;
  }

  const FOOT_TD_R = { padding: "11px 14px", fontSize: T.fsSm5, fontWeight: T.fwBold, color: T.dark, borderTop: `2px solid ${T.border}`, background: T.card };

  return (
    <>
      <style>{REPORT_TH_CSS}</style>
      <table style={{ ...OR_TABLE, tableLayout: "fixed" }}>
        <colgroup>
          <col style={{ width: "20%" }} />
          <col style={{ width: "14%" }} />
          <col style={{ width: "14%" }} />
          <col style={{ width: "28%" }} />
          <col style={{ width: "24%" }} />
        </colgroup>
        <thead style={OR_THEAD}>
          <tr>
            <th className="report-sort-th" style={leftTh("region")} onClick={() => handleSort("region")}>Region</th>
            <th className="report-sort-th" style={numTh("marketCount")}  onClick={() => handleSort("marketCount")}>Markets</th>
            <th className="report-sort-th" style={numTh("openRoles")}    onClick={() => handleSort("openRoles")}>Open roles</th>
            <th style={{ ...RT.th("left"), fontSize: T.fsXxs, fontWeight: T.fwBold, letterSpacing: ".07em", textTransform: "uppercase", color: "var(--v2-mid)", paddingLeft: 28 }}>Top department</th>
            <LegendTh
              label="Capacity mix"
              popup={CAPACITY_LEGEND}
              tooltipId="th-tooltip-capacity-status-region"
              tooltipText={REGION_CAPACITY_LEGEND_TEXT}
              thStyle={{ fontSize: T.fsXxs, fontWeight: T.fwBold, letterSpacing: ".07em", textTransform: "uppercase", color: "var(--v2-mid)" }}
            />
          </tr>
        </thead>
        <tbody>
          {sorted.map((r) => (
            <tr
              key={r.region}
              style={OR_TR}
              onMouseEnter={(e) => (e.currentTarget.style.background = T.hoverBg)}
              onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
            >
              <td style={{ ...OR_TD(), fontWeight: T.fwSemibold, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.region}</td>
              <td style={OR_TD("right")}>{r.marketCount}</td>
              <td style={{ ...OR_TD("right"), fontWeight: T.fwDisplay }}>{r.openRoles.toFixed(1)}</td>
              <td style={{ ...OR_TD(), paddingLeft: 28 }}>{r.topDept ?? <span style={{ color: T.light }}>—</span>}</td>
              <td style={OR_TD()}>
                <RegionCapacityChips
                  counts={capacityByRegion.get(r.region) ?? Object.fromEntries(REGION_CAPACITY_BUCKETS.map(({ key }) => [key, 0]))}
                  testId={`region-capacity-buckets-${r.region === "No region" ? "no-region" : r.region}`}
                />
              </td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr>
            <td style={FOOT_TD_R}>
              <span style={{ color: T.light, marginRight: 6 }}>=</span>
              All regions · total
            </td>
            <td style={{ ...FOOT_TD_R, textAlign: "right" }}>{totalMarkets}</td>
            <td style={{ ...FOOT_TD_R, textAlign: "right" }}>{totalReqs}</td>
            <td style={FOOT_TD_R} />
            <td style={FOOT_TD_R}>
              <RegionCapacityChips counts={portfolioCapacity} testId="region-capacity-buckets-total" />
            </td>
          </tr>
        </tfoot>
      </table>
    </>
  );
}

// ─── Field descriptors for useReportViewState ────────────────────────────────

const OR_VIEW_STATE_FIELDS = [
  { param: "view", valid: ["market", "department", "region"], default: "market" },
  { param: "q",                                                default: ""       },
];

// ─── Main component ───────────────────────────────────────────────────────────

/**
 * @param {{ onBack: () => void }} props
 */
function OpenRolesListOwner({ onBack, prevReport = null, nextReport = null, onNavigateReport }) {
  const spacePolicyState = useSpacePolicy();
  const featureContext = useFeatureFlagContext();
  const eligibility = getReportNavEligibility(featureContext);
  const marketsEnabled = eligibility.markets && eligibility.offices;
  const [data, setData]             = useState(null);
  const [monthlyHistory, setMonthlyHistory] = useState([]);
  const [monthlyHistoryLoading, setMonthlyHistoryLoading] = useState(true);
  const [monthlyHistoryError, setMonthlyHistoryError] = useState(null);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState(null);
  const [animKey, setAnimKey]       = useState(0);

  // Display state encoded in URL + persisted to localStorage.
  const [rvs, setRvs] = useReportViewState({
    storageKey: REPORT_OPEN_ROLES_KEY,
    fields: OR_VIEW_STATE_FIELDS,
  });
  const search = rvs.q;
  const viewBy = !marketsEnabled && ["market", "region"].includes(rvs.view) ? "department" : rvs.view;
  useEffect(() => {
    if (!eligibility.ready) {
      setData(null);
      setLoading(false);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    getOpenRolesReport()
      .then((d)  => { if (!cancelled) { setData(d); setError(null); setAnimKey((k) => k + 1); } })
      .catch((e) => { if (!cancelled) setError(e); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [eligibility.ready]);

  useEffect(() => {
    if (!eligibility.ready) {
      setMonthlyHistory([]);
      setMonthlyHistoryLoading(false);
      return undefined;
    }
    let cancelled = false;
    setMonthlyHistoryLoading(true);
    getOpenRolesMonthlyHistory()
      .then((result) => {
        if (!cancelled) {
          setMonthlyHistory(Array.isArray(result?.snapshots) ? result.snapshots : []);
          setMonthlyHistoryError(null);
        }
      })
      .catch((historyError) => {
        if (!cancelled) setMonthlyHistoryError(historyError);
      })
      .finally(() => {
        if (!cancelled) setMonthlyHistoryLoading(false);
      });
    return () => { cancelled = true; };
  }, [eligibility.ready]);

  const kpis         = data?.kpis ?? {};
  const markets      = data?.markets ?? [];
  const departments  = data?.departments ?? [];
  const regions      = data?.regions ?? [];
  const remoteCount    = data?.remote ?? 0;
  const unmatchedCount = data?.unmatched ?? 0;
  const freshness    = data?.freshness ?? {};

  // Known Over-Cap / Tight markets, using assigned-today inventory rather than
  // the independent forecast-pressure fields in the hiring report.
  const inPressure = useMemo(() => {
    if (!markets.length) return 0;
    if (!markets.some(m => computeMarketSpaceFit(m).verdict)) return null;
    return Math.round(
      markets
        .filter((r) => {
          const { verdict } = computeMarketSpaceFit(r);
          return verdict === SPACE_FIT_VERDICT_OVER || verdict === SPACE_FIT_VERDICT_TIGHT;
        })
        .reduce((sum, r) => sum + r.openRoles, 0) * 10
    ) / 10;
  }, [markets, spacePolicyState.baseline]);

  // Insight banner: fires when ≥ 20% of office-based roles land in over/dense markets.
  const openRolesInsight = useMemo(
    () => computeOpenRolesInsight(markets, kpis),
    [markets, kpis, spacePolicyState.baseline],
  );

  if (!eligibility.ready) {
    return <ReportAccessNotice reportName="Open Roles" loading onBack={onBack} />;
  }

  return (
    <ReportShell
        recordNavigation
        onBack={onBack}
        backTestId="open-roles-back"
        prevReport={prevReport}
        nextReport={nextReport}
        onNavigateReport={onNavigateReport}
      >

      {/* ── Body ─────────────────────────────────────────────────────────── */}
      <div style={{ maxWidth: 1400, margin: "0 auto", padding: "0 32px 48px", width: "100%" }}>

        <ErrorBanner error={error} onDismiss={() => setError(null)} />

        {/* KPI tiles — top of body, above search row */}
        {!loading && data && (
          <div style={{ marginBottom: 16 }}>
            <KpiBand testId="open-roles-kpi-row" ariaLabel="Report metrics">
              <KpiTile
                testId="open-roles-kpi-total"
                label="Open roles"
                rawValue={typeof kpis.totalReqs === "number" ? kpis.totalReqs : 0}
                formatter={(n) => fmtInt(n)}
                animationKey={animKey}
                tileIndex={0}
                sub="unique requisitions · Greenhouse"
              />
              {eligibility.offices && <KpiTile
                testId="open-roles-kpi-office-based"
                label="Office-based"
                rawValue={typeof kpis.officeBased === "number" ? kpis.officeBased : 0}
                formatter={(n) => n.toFixed(1)}
                animationKey={animKey}
                tileIndex={1}
                sub="weighted · adds seat demand"
              />}
              <KpiTile
                testId="open-roles-kpi-remote"
                label="Remote"
                rawValue={typeof kpis.remote === "number" ? kpis.remote : 0}
                formatter={(n) => n.toFixed(1)}
                animationKey={animKey}
                tileIndex={2}
                sub="weighted · no office"
              />
              {marketsEnabled && <KpiTile
                testId="open-roles-kpi-in-pressure"
                label="In Over-Cap / Tight markets"
                rawValue={inPressure}
                formatter={(n) => inPressure == null ? "—" : fmtInt(n)}
                animationKey={animKey}
                tileIndex={3}
                sub={inPressure == null ? "Space Fit unavailable" : markets.some(m => {
                  const fit = computeMarketSpaceFit(m);
                  return !fit.verdict || fit.partial;
                }) ? "Partial — known market Space Fit" : "known market Space Fit"}
              />}
              <KpiTile
                testId="open-roles-kpi-departments"
                label="Departments"
                rawValue={typeof kpis.departments === "number" ? kpis.departments : 0}
                formatter={(n) => fmtInt(n)}
                animationKey={animKey}
                tileIndex={4}
                sub="unique hiring depts"
              />
            </KpiBand>
          </div>
        )}

        {/* Compact insight strip — appears below KPI tiles when pressure threshold fires */}
        {!loading && marketsEnabled && openRolesInsight && (
          <CompactInsightStrip
            headline={`${openRolesInsight.viz.pressureCount} of ${openRolesInsight.viz.pressureCount + openRolesInsight.viz.roomCount} open roles (${openRolesInsight.metric}) are landing in over- or near-capacity markets - hiring is out-running space in those markets.`}
            viz={openRolesInsight.viz}
            deepLink={openRolesInsight.deepLink}
            action={openRolesInsight.action}
          />
        )}

        {/* Toolbar — Pivot row + Query row */}
        {!loading && data && (
          <div style={{ marginBottom: 12 }}>
            {/* Row 1: Pivot row — View by eyebrow + toggle */}
            <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 8, flexWrap: "wrap" }}>
              <span style={{
                fontSize: T.fsXxs,
                fontWeight: T.fwBold,
                letterSpacing: "0.05em",
                textTransform: "uppercase",
                color: T.light,
              }}>
                View by:
              </span>
              <ToolbarViewToggle
                segments={marketsEnabled ? VIEW_SEGMENTS : VIEW_SEGMENTS.filter((segment) => segment.key === "department")}
                value={viewBy}
                onChange={(v) => setRvs({ view: v })}
                testId="open-roles-view-toggle"
                ariaLabel="View by"
              />
            </div>
            {/* Row 2: Query row — Search on the left */}
            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", minHeight: T.toolbarRowH }}>
              <input
                type="search"
                data-testid="open-roles-search"
                aria-label="Search this report"
                placeholder="Search this report"
                value={search}
                onChange={(e) => setRvs({ q: e.target.value })}
                style={{
                  width: 230,
                  padding: "7px 10px",
                  fontSize: T.fsSm5 ?? T.fsSm,
                  border: `1px solid ${T.border ?? T.lightGray}`,
                  borderRadius: 8,
                  color: T.dark,
                  background: T.white,
                  boxSizing: "border-box",
                }}
              />
            </div>
          </div>
        )}

        {/* Freshness bar — below toolbar, above table */}
        {data && (
          <FreshnessBar
            freshness={freshness}
          />
        )}

        {data && (
          <OpenRolesMonthlyTrend
            snapshots={monthlyHistory}
            loading={monthlyHistoryLoading}
            error={monthlyHistoryError}
            showMarkets={marketsEnabled}
            showOfficeBased={eligibility.offices}
          />
        )}

        {/* Table area — no inner scroll container; page scrolls naturally */}
        <Card style={{ marginTop: 4 }}>
          {loading && <div style={S.emptyState}>Loading…</div>}
          {!loading && !data && !error && <div style={S.emptyState}>No data.</div>}
          {!loading && data && viewBy === "market" && (
            <MarketTable
              rows={markets}
              remoteCount={remoteCount}
              unmatchedCount={unmatchedCount}
              search={search}
              totalReqs={kpis.totalReqs}
            />
          )}
          {!loading && data && viewBy === "department" && (
            departments.length > 0
              ? <DepartmentTable
                  departments={departments}
                  search={search}
                  totalReqs={kpis.totalReqs}
                  officeBased={kpis.officeBased}
                  remote={kpis.remote}
                  marketsEnabled={marketsEnabled}
                  officesEnabled={eligibility.offices}
                />
              : <div style={{ ...S.emptyState, padding: "40px 24px" }}>
                  <div style={{ fontWeight: T.fwSemibold, color: T.dark, marginBottom: 6 }}>
                    No department data available
                  </div>
                  <div style={{ color: T.mid, fontSize: T.fsSm }}>
                    Department breakdown will appear once Greenhouse roles include department tags.
                  </div>
                </div>
          )}
          {!loading && data && marketsEnabled && viewBy === "region" && (
            regions.length > 0
              ? <RegionTable
                  regions={regions}
                  search={search}
                  totalReqs={kpis.totalReqs}
                  markets={markets}
                />
              : <div style={{ ...S.emptyState, padding: "40px 24px" }}>
                  <div style={{ fontWeight: T.fwSemibold, color: T.dark, marginBottom: 6 }}>
                    No region data available
                  </div>
                  <div style={{ color: T.mid, fontSize: T.fsSm }}>
                    Region breakdown requires markets to have regions assigned.
                  </div>
                </div>
          )}
        </Card>
      </div>
    </ReportShell>
  );
}
